/**
 * Top-left phase countdown widget.
 *
 * Big Oswald numerals + a label identifying the current phase. Shows the
 * remaining seconds for whichever phase is active:
 *
 *   - poll_open(phase=category)   -> "VOTE" with category_vote_seconds
 *   - poll_open(phase=clue_value) -> "PICK" with clue_answer_seconds
 *   - clue_reveal                 -> "ANSWER" counting down to hard_end
 *   - grace_begin                 -> "GRACE" counting down to grace_end
 *   - round_transition            -> "NEXT ROUND" counting down the splash
 *
 * Any other scene (lobby, podium, board-between-clues with no active
 * countdown) hides the widget. Clients drive the widget via setPhase(label,
 * endsAtLocalMs) — the widget then ticks on its own RAF loop.
 *
 * @packageDocumentation
 */

const STYLES = `
  @keyframes sg-countdown-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes sg-countdown-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.08); }
  }
  .sg-countdown {
    position: absolute;
    top: 40px;
    left: 40px;
    z-index: 60;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    pointer-events: none;
    padding: 10px 22px 8px 22px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(6,12,233,0.62) 0%, rgba(3,4,40,0.82) 100%);
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 4px 30px rgba(0,0,0,0.4);
    animation: sg-countdown-in 260ms ease-out;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    color: #fff;
    min-width: 160px;
  }
  .sg-countdown.hidden { display: none; }
  .sg-countdown-value {
    font-size: 92px;
    font-weight: 700;
    line-height: 0.95;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 18px rgba(255,196,61,0.55), 0 3px 6px rgba(0,0,0,0.6);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
  }
  .sg-countdown-label {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.28em;
    color: var(--sg-accent, #00F0FF);
    text-transform: uppercase;
    text-shadow: 0 2px 4px rgba(0,0,0,0.6);
  }
  .sg-countdown.sg-warn .sg-countdown-value {
    animation: sg-countdown-pulse 600ms ease-in-out infinite;
  }
  .sg-countdown.sg-danger {
    border-color: #F87171;
  }
  .sg-countdown.sg-danger .sg-countdown-value {
    color: #F87171;
    text-shadow: 0 0 20px rgba(248,113,113,0.7), 0 3px 6px rgba(0,0,0,0.6);
    animation: sg-countdown-pulse 420ms ease-in-out infinite;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('sg-countdown-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-countdown-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export interface CountdownHandle {
  el: HTMLDivElement;
  /**
   * Arm the countdown for a new phase. `endsAtLocalMs` is in performance.now()
   * space (the same coordinate system `ClockSync.toLocalMs` returns).
   * The third argument is accepted for backward compatibility with callers
   * that still pass the legacy `countdown_starts_at_ms` grace marker — it is
   * intentionally ignored. The widget now ticks one continuous number from
   * the very first second of the phase down to 0; the backend may still
   * include a read-time grace inside `endsAtLocalMs`, but the visible
   * countdown stays a single number throughout (no "READY" two-phase split).
   * Calling with the same label+endsAt is a no-op so repeated poll_tally
   * frames don't restart the intro animation.
   */
  setPhase: (label: string, endsAtLocalMs: number, startsAtLocalMs?: number) => void;
  /** Hide and stop the RAF. */
  hide: () => void;
  dispose: () => void;
}

export function createCountdown(mount: HTMLElement): CountdownHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'sg-countdown hidden';
  el.innerHTML = `
    <div class="sg-countdown-value" data-role="value">0</div>
    <div class="sg-countdown-label" data-role="label"></div>
  `;
  mount.appendChild(el);
  const valueEl = el.querySelector<HTMLDivElement>('[data-role="value"]')!;
  const labelEl = el.querySelector<HTMLDivElement>('[data-role="label"]')!;

  let endsAt = 0;
  let startsAt = 0;
  let currentLabel = '';
  let raf: number | null = null;

  function renderTick(): void {
    const now = performance.now();
    if (startsAt > 0 && now < startsAt) {
      const fullMs = Math.max(0, endsAt - startsAt);
      const str = String(Math.ceil(fullMs / 1000));
      if (valueEl.textContent !== str) valueEl.textContent = str;
      el.classList.remove('sg-warn', 'sg-danger');
      raf = requestAnimationFrame(renderTick);
      return;
    }
    const remainingMs = Math.max(0, endsAt - now);
    const seconds = Math.ceil(remainingMs / 1000);
    const str = String(seconds);
    if (valueEl.textContent !== str) valueEl.textContent = str;
    // Danger under 3s, warn under 5s; compute against floating remaining.
    const remSec = remainingMs / 1000;
    el.classList.toggle('sg-warn', remSec <= 5 && remSec > 3);
    el.classList.toggle('sg-danger', remSec <= 3);
    if (remainingMs <= 0) {
      valueEl.textContent = '0';
      raf = null;
      return;
    }
    raf = requestAnimationFrame(renderTick);
  }

  function setPhase(label: string, endsAtLocalMs: number, startsAtLocalMs?: number): void {
    const normalizedLabel = label.toUpperCase();
    // Defense in depth: clamp if caller passed raw Date.now() (skew > 120s).
    const now = performance.now();
    const skew = endsAtLocalMs - now;
    const resolvedEnd = (skew > 120_000 || skew < -10_000) ? now + 60_000 : endsAtLocalMs;
    const resolvedStart = (() => {
      if (startsAtLocalMs == null) return 0;
      const startSkew = startsAtLocalMs - now;
      if (startSkew > 120_000 || startSkew < -10_000) return 0;
      return startsAtLocalMs > now ? startsAtLocalMs : 0;
    })();
    // Idempotent within ~200ms to suppress poll_tally re-arm flicker.
    if (
      normalizedLabel === currentLabel &&
      Math.abs(resolvedEnd - endsAt) < 250 &&
      Math.abs(resolvedStart - startsAt) < 250
    ) {
      if (el.classList.contains('hidden')) el.classList.remove('hidden');
      if (raf === null) renderTick();
      return;
    }
    currentLabel = normalizedLabel;
    endsAt = resolvedEnd;
    startsAt = resolvedStart;
    labelEl.textContent = normalizedLabel;
    el.classList.remove('hidden');
    el.classList.remove('sg-warn', 'sg-danger');
    // Re-trigger the intro animation.
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    if (raf !== null) cancelAnimationFrame(raf);
    renderTick();
  }

  function hide(): void {
    el.classList.add('hidden');
    currentLabel = '';
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  }

  return {
    el,
    setPhase,
    hide,
    dispose() {
      hide();
      el.remove();
    },
  };
}
